import {
  Box,
  Stack,
  Text,
  Image,
  Container,
  HStack,
  Icon,
  Center,
  Show,
  Link as ChakraLink,
} from '@chakra-ui/react';
import { Twitter } from '@styled-icons/boxicons-logos/Twitter';
import { Youtube } from '@styled-icons/boxicons-logos/Youtube';
import { Discord } from '@styled-icons/boxicons-logos/Discord';
import { Article } from '@styled-icons/material/Article';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import footerLogo from '@/assets/images/white-logo.svg';
import Link from '@/components/Link';

interface FooterProps {
  corporateSiteRoot: string;
}

const Footer: React.FC<FooterProps> = ({ corporateSiteRoot }) => {
  const { t, i18n } = useTranslation();
  return (
    <Box bg="blue.500">
      <Box
        bg="gray.700"
        h="70px"
        mt="-35px"
        sx={{ transformOrigin: '100% 0', transform: 'skewY(-1deg)' }}
      />
      <Box bg="gray.700">
        <Container maxW="container.lg" py={[0, 0, 4]}>
          <Stack
            align="center"
            direction={['column-reverse', 'column-reverse', 'row']}
            justify="space-between"
            py={4}
            spacing="8">
            <Stack align="center" direction={['column', 'row']} spacing="24">
              <Show above="md">
                <Link as={RouterLink} to="/home">
                  <Image alt="Pretia Technologies" boxSize="60px" src={footerLogo} />
                </Link>
              </Show>
              <Stack spacing="4">
                <ChakraLink
                  variant="white-link"
                  href={corporateSiteRoot}
                  rel="noreferrer"
                  target="_blank">
                  {t('company')}
                </ChakraLink>
                <ChakraLink
                  variant="white-link"
                  href={`${corporateSiteRoot}/inquiry`}
                  rel="noreferrer"
                  target="_blank">
                  {t('inquiry')}
                </ChakraLink>
                <ChakraLink
                  variant="white-link"
                  href={
                    i18n.language === 'jp'
                      ? 'https://arcloud.pretiaar.com/privacy-policy-jp.pdf'
                      : 'https://arcloud.pretiaar.com/privacy-policy-en.pdf'
                  }
                  rel="noreferrer"
                  target="_blank">
                  {t('privacy_policy')}
                </ChakraLink>
                <ChakraLink
                  variant="white-link"
                  href="https://s3.amazonaws.com/arcloud.pretiaar.com/opensource/OSS.zip"
                  rel="noreferrer"
                  target="_blank">
                  {t('open_source')}
                </ChakraLink>
              </Stack>
            </Stack>
            <HStack align="center" h="100%" py="0" spacing={[12, 12, 4]}>
              <Link aria-label="Twitter" href="https://twitter.com/pretia_official" target="_blank">
                <Box
                  _hover={{ color: 'teal.500' }}
                  alignItems="center"
                  bg="white"
                  color="teal.600"
                  h="10"
                  justifyContent="center"
                  p="1"
                  rounded="full"
                  w="10">
                  <Icon as={Twitter} boxSize="8" />
                </Box>
              </Link>
              <Link
                aria-label="Discord"
                href={import.meta.env.VITE_DISCORD_INVITATION_LINK}
                target="_blank">
                <Box
                  _hover={{ color: 'teal.500' }}
                  alignItems="center"
                  bg="white"
                  color="teal.600"
                  h="10"
                  justifyContent="center"
                  p="1"
                  rounded="full"
                  w="10">
                  <Icon as={Discord} boxSize="8" />
                </Box>
              </Link>
              <Link
                aria-label="Youtube"
                href="https://www.youtube.com/channel/UCJRJQl3SlEQKNe6EylX0pFw"
                target="_blank">
                <Box
                  _hover={{ color: 'teal.500' }}
                  alignItems="center"
                  bg="white"
                  color="teal.600"
                  h="10"
                  justifyContent="center"
                  p="1"
                  rounded="full"
                  w="10">
                  <Icon as={Youtube} boxSize="8" />
                </Box>
              </Link>
              <Link aria-label="Blog" href="https://note.com/pretia_tech/" target="_blank">
                <Box
                  _hover={{ color: 'teal.500' }}
                  alignItems="center"
                  bg="white"
                  color="teal.600"
                  h="10"
                  justifyContent="center"
                  p="1"
                  rounded="full"
                  w="10">
                  <Icon as={Article} boxSize="8" />
                </Box>
              </Link>
            </HStack>
          </Stack>
          <Show below="md">
            <Center my="8">
              <Link as={RouterLink} to="/home">
                <Image alt="Pretia Technologies" boxSize="60px" src={footerLogo} />
              </Link>
            </Center>
          </Show>
          <Stack p="8">
            <Center>
              <Text align="center" color="white" fontSize="xs">
                {t('pretia_copyrights')}
              </Text>
            </Center>
          </Stack>
        </Container>
      </Box>
    </Box>
  );
};

export default Footer;
